import React from 'react';
import {
  Box,
  Button,
  Paper,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

const stepIconByIndex = {
  1: FolderIcon,
  2: GroupIcon,
  3: AssignmentIcon,
};

function WorkflowStepIcon(props) {
  const { active, completed, className, icon } = props;
  const theme = useTheme();
  const Icon = stepIconByIndex[Number(icon)] || FolderIcon;

  const bg = completed
    ? theme.palette.success.main
    : active
      ? theme.palette.primary.main
      : theme.palette.grey[200];

  const fg = completed || active ? theme.palette.common.white : theme.palette.text.secondary;

  return (
    <Box
      className={className}
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: bg,
        color: fg,
        boxShadow: active ? '0 10px 18px rgba(0,0,0,0.18)' : 'none',
        transform: active ? 'translateY(-1px)' : 'none',
        transition: 'all 180ms ease',
      }}
    >
      <Icon fontSize="small" />
    </Box>
  );
}

function getActiveStep(pathname) {
  if (pathname.startsWith('/requests')) return 2;
  if (pathname.startsWith('/cohorts')) return 1;
  if (pathname.startsWith('/catalog')) return 0;
  if (pathname.startsWith('/parameter')) return 0;
  if (pathname.startsWith('/cart')) return 0;
  return 0; // On dashboard, default to "start here"
}

export default function WorkflowGuide() {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const activeStep = getActiveStep(location.pathname);
  const steps = [
    {
      key: 'catalog',
      label: t('workflowGuide.steps.catalog'),
      path: '/catalog',
      tooltip: t('workflowGuide.tooltips.catalog'),
    },
    {
      key: 'cohorts',
      label: t('workflowGuide.steps.cohorts'),
      path: '/cohorts',
      tooltip: t('workflowGuide.tooltips.cohorts'),
    },
    {
      key: 'requests',
      label: t('workflowGuide.steps.requests'),
      path: '/requests',
      tooltip: t('workflowGuide.tooltips.requests'),
    },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 3,
        borderRadius: 2,
        borderColor: 'divider',
        backgroundImage:
          'radial-gradient(1200px 400px at 20% -10%, rgba(237, 125, 49, 0.10), transparent 60%), radial-gradient(900px 350px at 100% 0%, rgba(0, 184, 212, 0.10), transparent 55%)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {t('workflowGuide.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('workflowGuide.subtitle')}
          </Typography>
        </Box>

        <Button
          component={RouterLink}
          to="/help"
          size="small"
          startIcon={<InfoOutlinedIcon />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {t('workflowGuide.learnMore')}
        </Button>
      </Box>

      <Stepper
        nonLinear
        activeStep={activeStep}
        alternativeLabel={!isSmall}
        sx={{
          px: { xs: 0, sm: 1 },
          '& .MuiStepConnector-line': { borderColor: 'divider' },
        }}
      >
        {steps.map((step, idx) => (
          <Step key={step.key} completed={activeStep > idx}>
            <Tooltip title={step.tooltip} arrow placement="top">
              <StepButton onClick={() => navigate(step.path)} sx={{ px: 1 }}>
                <StepLabel StepIconComponent={WorkflowStepIcon}>{step.label}</StepLabel>
              </StepButton>
            </Tooltip>
          </Step>
        ))}
      </Stepper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        <Trans
          i18nKey="workflowGuide.tip"
          components={{ catalog: <b />, cohort: <b />, request: <b /> }}
        />
      </Typography>
    </Paper>
  );
}
